import React, {
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react"
import useMeasure from "react-use-measure"
import {
  useReactTable,
  getCoreRowModel,
  type Table,
  type ColumnSizingState,
} from "@tanstack/react-table"

import { type Virtualizer } from "@tanstack/react-virtual"
import { useLocalStorageState } from "ahooks"
import { GuestSummary } from "sharedTypes"
import { useShallow } from "zustand/shallow"
import useGuestsStore from "services/useGuestsStore"
import useTableVirtualization from "./useTableVirtualization"
import { useColumnVisibilityContext } from "../../ColumnVisibilityProvider"
import useTableColumns from "./useTableColumns"
import useAvailableColumns from "./useAvailableColumns"
import {
  flattenGuestsWithPlusOnes,
  calculateColumnWidths,
  findCurrentSortOption,
  getNextSortOrder,
  type ExtendedGuestSummary,
} from "./helpers"

const coreRowModel = getCoreRowModel<ExtendedGuestSummary>()

export type UseDataTableArgs = {
  guests: GuestSummary[];
  order: string;
  allSelected: boolean;
  multipleSelect?: boolean;
  disabledGuestIds: string[];
  disableMainGuests: boolean;
  filtered: boolean;
  timezone: string;
  onScrollEnd?: () => void;
  toggleSelectAll: () => void;
  onSelectionChange: (
    event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
    guest: GuestSummary,
    checked?: boolean,
  ) => void;
  onSetOrder: (order: string) => void;
}

export type UseDataTableReturn = {
  tableContainerRef: (node: HTMLDivElement | null) => void;
  columnData: { widths: Record<string, number>; totalWidth: number; shouldScroll: boolean };
  getColumnWidth: (columnId: string) => string;
  table: Table<ExtendedGuestSummary>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export const useDataTable = (args: UseDataTableArgs): UseDataTableReturn => {
  const {
    guests,
    order,
    allSelected,
    multipleSelect = false,
    disabledGuestIds = [],
    disableMainGuests,
    filtered,
    timezone,
    onScrollEnd,
    toggleSelectAll,
    onSelectionChange,
    onSetOrder,
  } = args
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [measureRef, tableBounds] = useMeasure()

  const tableContainerRef = useCallback((node: HTMLDivElement | null) => {
    measureRef(node)
    scrollContainerRef.current = node
  }, [measureRef])

  const availableColumnConfigs = useAvailableColumns()

  const flattenedGuests = useMemo(() => flattenGuestsWithPlusOnes(guests, !filtered),
    [guests, filtered])

  // Scroll to top when the dataset changes (segment switch, sort, search)
  // but not on infinite scroll appends (first guest stays the same).
  const firstGuestId = guests[0]?.id
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 })
  }, [firstGuestId])

  const { selectedGuestIds } = useGuestsStore(
    useShallow((state) => ({ selectedGuestIds: state.selectedGuestIds })),
  )

  const selectedGuestIdsSet = useMemo(
    () => new Set(selectedGuestIds),
    [selectedGuestIds],
  )

  const { getVisibleColumns } = useColumnVisibilityContext()

  const { tableColumns } = useTableColumns({
    availableColumnConfigs,
  })

  const visibleColumns = useMemo(
    () => getVisibleColumns().filter((colId) => colId in availableColumnConfigs),
    [getVisibleColumns, availableColumnConfigs],
  )

  const [columnSizing = {}, setColumnSizing] = useLocalStorageState<ColumnSizingState>(
    "guestsTableColumnSizing",
    { defaultValue: {} },
  )

  const columnData = useMemo(() => {
    const containerWidth = tableBounds.width || 0

    return calculateColumnWidths(
      visibleColumns, availableColumnConfigs, containerWidth, columnSizing,
    )
  }, [visibleColumns, availableColumnConfigs, tableBounds.width, columnSizing])

  const currentSorting = useMemo(() => {
    if (!order) return []

    const sortOption = findCurrentSortOption(order)
    if (!sortOption) return []

    return [{ id: sortOption.columnId, desc: sortOption.direction === "desc" }]
  }, [order])

  const table = useReactTable({
    columns: tableColumns,
    data: flattenedGuests,
    getCoreRowModel: coreRowModel,
    enableSorting: true,
    manualSorting: true,
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    onSortingChange: (updater) => {
      const newSorting = typeof updater === "function" ? updater(currentSorting) : updater

      // Get column ID from the new sorting, or from the current sorting
      // when TanStack sends an empty array (its internal desc → unsorted toggle)
      const columnId = newSorting[0]?.id ?? currentSorting[0]?.id
      if (!columnId) {
        onSetOrder("")

        return
      }

      const nextOrder = getNextSortOrder(columnId, order)
      onSetOrder(nextOrder || "")
    },
    onColumnSizingChange: () => {
      // TanStack computes new size from its internal column.getSize() + delta.
      // We need to correct this: use our rendered width as the base instead.
      const info = table.getState().columnSizingInfo
      const colId = info.isResizingColumn
      if (colId && info.deltaOffset != null) {
        const renderedWidth = columnData.widths[colId] || 150
        const minWidth = availableColumnConfigs[colId]?.minWidth || 100
        const newWidth = Math.max(minWidth, renderedWidth + info.deltaOffset)
        setColumnSizing({ ...columnSizing, [colId]: newWidth })
      }
    },
    state: {
      sorting: currentSorting,
      columnPinning: { left: ["name"] },
    },
    meta: {
      selectedGuestIds,
      selectedGuestIdsSet,
      allSelected,
      onSelectionChange,
      toggleSelectAll,
      multipleSelect,
      disableMainGuests,
      disabledGuestIds,
      order,
      timezone,
      resetColumnSize: (columnId: string) => {
        const { [columnId]: _, ...rest } = columnSizing
        setColumnSizing(rest)
      },
    },
    defaultColumn: { size: 150, minSize: 100, maxSize: 500 },
  })

  const { isResizingColumn, deltaOffset } = table.getState().columnSizingInfo

  const getColumnWidth = useCallback(
    (columnId: string) => {
      const baseWidth = columnData.widths[columnId] || 150
      if (isResizingColumn === columnId && deltaOffset != null) {
        const minW = availableColumnConfigs[columnId]?.minWidth || 100

        return `${Math.max(minW, baseWidth + deltaOffset)}px`
      }

      return `${baseWidth}px`
    },
    [columnData.widths, isResizingColumn, deltaOffset, availableColumnConfigs],
  )

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return undefined

    const handleScroll = () => {
      const scrolledLeft = el.scrollLeft > 0
      const scrolledTop = el.scrollTop > 0
      el.toggleAttribute("data-scrolled-left", scrolledLeft)
      el.toggleAttribute("data-scrolled-top", scrolledTop)
    }

    el.addEventListener("scroll", handleScroll, { passive: true })

    return () => el.removeEventListener("scroll", handleScroll)
  }, [])

  const rowVirtualizer = useTableVirtualization({
    scrollContainerRef,
    table,
    onScrollEnd,
  })

  return {
    tableContainerRef,
    columnData,
    getColumnWidth,
    table,
    rowVirtualizer,
  }
}
